import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { Loader2, Search, Pencil, Check, ChevronRight, PenLine } from 'lucide-react';
import { crossfitResultsService, CrossFitResult } from '../services/crossfit-results.service';
import SignatureViewer from '../components/SignatureViewer';
import apiService from '../services/api.service';
import { useRealtime } from '../contexts/RealtimeContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../components/ui/sheet';
import { cn } from '../lib/utils';
import { formaterKg, lireCharge } from '../lib/charges';

interface WodPart {
  part_number: number;
  name: string;
  scoring_type?: string; // time | weight | amrap | reps | ladder
}

interface Wod {
  id: string;
  name: string;
  wod_number: number;
  scoring_type?: string;
  parts_count: number;
  parts: WodPart[];
}

interface Category {
  id: string;
  name: string;
}

interface PartScore {
  id: string;
  reps_completed: number;
  reps_remaining: number | null;
  finish_time_seconds: number | null;
  announced_weight: number | null;
  is_finished: boolean;
  cap_reached: boolean;
  is_dnf: boolean;
  is_dns?: boolean; // LOG-238 : équipe absente (0 point)
  tiebreak_time: number | null;
  category_points?: number; // Points de cette partie
  // Contexte de contestation (accordéon, revue UX 2026-08-03)
  heat_id?: string | null;
  heat_number?: number | null;
  lane_number?: number | null;
  finalized_at?: string | null;
}

interface TeamResult {
  team_id: string;
  team_name: string;
  category_id: string;
  category_name: string;
  scores: Record<number, PartScore>;
  total_reps: number;
  points: number;
  rank: number | null;
}

type ResultView = 'general' | 'by-wod';

// ===== Helpers score (selon scoring_type) =====

// Formate des secondes en mm:ss
const fmtMMSS = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
};

// Parse "mm:ss" (ou un nombre de secondes brut) en secondes
const parseTimeToSeconds = (input: string): number => {
  const t = input.trim();
  if (t.includes(':')) {
    const [m, s] = t.split(':');
    return (parseInt(m) || 0) * 60 + (parseInt(s) || 0);
  }
  return parseInt(t) || 0;
};

// Affichage du score selon le scoring_type de la partie
const formatScoreDisplay = (score: PartScore | undefined, scoringType?: string): string => {
  if (!score) return '-';
  if (score.is_dns) return 'DNS';
  const st = scoringType || 'reps';
  if (st === 'time') {
    // Terminé -> temps de complétion ; cappé -> reps faites
    if (score.is_finished && score.finish_time_seconds && score.finish_time_seconds > 0) {
      return fmtMMSS(score.finish_time_seconds);
    }
    return score.reps_completed ? `${score.reps_completed} reps` : '-';
  }
  if (st === 'weight') {
    return score.announced_weight != null ? `${formaterKg(score.announced_weight)} kg` : '-';
  }
  // amrap / reps / ladder
  return score.reps_completed ? `${score.reps_completed}` : '-';
};

// ===== Cellule score : lecture seule OU éditable (selon `readOnly`) =====
interface ScoreEditCellProps {
  score: PartScore | undefined;
  scoringType?: string;
  readOnly?: boolean;
  // 'inline' : popover tableur (desktop) ; 'sheet' : bottom sheet (mobile).
  layout?: 'inline' | 'sheet';
  // Contexte affiché dans l'en-tête du bottom sheet (ex. « Alpha Force · open 26.2 »).
  label?: string;
  onSave: (resultId: string, payload: Record<string, unknown>) => Promise<void>;
}

const ScoreEditCell: React.FC<ScoreEditCellProps> = ({ score, scoringType, readOnly, layout = 'inline', label, onSave }) => {
  const { t } = useTranslation();
  const st = scoringType || 'reps';
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inputOffset, setInputOffset] = useState<number | null>(null);
  const [finished, setFinished] = useState<boolean>(!!score?.is_finished);
  const [timeStr, setTimeStr] = useState<string>(score?.finish_time_seconds ? fmtMMSS(score.finish_time_seconds) : '');
  const [reps, setReps] = useState<string>(score?.reps_completed ? String(score.reps_completed) : '');
  const [weight, setWeight] = useState<string>(score?.announced_weight != null ? String(score.announced_weight) : '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);
  const commitRef = useRef<() => void>(() => {});

  // Resync depuis les données après rechargement.
  // LOG-166 : ne PAS resynchroniser une cellule dont l'éditeur est OUVERT —
  // le refetch silencieux post-sauvegarde (d'une autre cellule) écrasait la
  // saisie en cours par la valeur serveur.
  useEffect(() => {
    if (isEditing) return;
    setFinished(!!score?.is_finished);
    setTimeStr(score?.finish_time_seconds ? fmtMMSS(score.finish_time_seconds) : '');
    setReps(score?.reps_completed ? String(score.reps_completed) : '');
    setWeight(score?.announced_weight != null ? String(score.announced_weight) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score]);

  // Clic en dehors du popover = valider (comportement tableur). commitRef pointe
  // toujours sur la dernière closure -> pas de valeurs périmées.
  useEffect(() => {
    if (!isEditing || layout === 'sheet') return; // sheet : validation via bouton, pas au clic extérieur
    committedRef.current = false;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        commitRef.current();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isEditing, layout]);

  // Décale le popover pour que le champ éditable tombe pile sur le centre de la
  // colonne (au lieu de centrer tout le popover, ce qui décalerait le champ).
  useLayoutEffect(() => {
    if (!isEditing) { setInputOffset(null); return; }
    const el = inputRef.current;
    if (el) setInputOffset(el.offsetLeft + el.offsetWidth / 2);
  }, [isEditing, st, finished]);

  if (!score) return <span className="text-muted-foreground">-</span>;

  // Mode lecture seule : simple affichage, aucune interaction (utilisé hors édition).
  if (readOnly) {
    return (
      <span className={cn('tabular-nums', score.is_dns && 'text-destructive')}>
        {formatScoreDisplay(score, st)}
        {st === 'time' && !score.is_dns && !score.is_finished && score.reps_completed > 0 && (
          <span className="ml-1 text-[10px] text-muted-foreground align-middle">CAP</span>
        )}
      </span>
    );
  }

  const persist = async (payload: Record<string, unknown>) => {
    setIsSaving(true);
    try {
      await onSave(score.id, payload);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  // Saisir un score remet l'équipe présente (LOG-238).
  const buildPayload = (): Record<string, unknown> => {
    if (st === 'time') {
      // Terminé : classé sur le temps (cap_reached forcé à false) ; Cappé : classé sur les reps
      return finished
        ? { is_dns: false, is_finished: true, cap_reached: false, finish_time_seconds: parseTimeToSeconds(timeStr) }
        : { is_dns: false, is_finished: false, cap_reached: true, reps_completed: parseInt(reps) || 0 };
    }
    if (st === 'weight') return { is_dns: false, announced_weight: lireCharge(weight) || 0 };
    return { is_dns: false, reps_completed: parseInt(reps) || 0 };
  };

  // LOG-238 : bascule absente / présente, sans toucher au score saisi.
  const toggleAbsent = () => {
    committedRef.current = true;
    void persist({ is_dns: !score.is_dns });
  };
  const absentLabel = score.is_dns ? t('results.markPresent') : t('results.markAbsent');

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    void persist(buildPayload());
  };
  const cancel = () => {
    committedRef.current = true;
    setIsEditing(false);
  };
  commitRef.current = commit;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const inputCls = 'w-20 h-8 bg-transparent text-center border-border/70 focus-visible:ring-2 focus-visible:ring-ring';

  // Aperçu du rendu final : l'éditeur saisit les reps faites, mais les
  // résultats affichent « CAP +restantes » (ou le chrono si Terminé) → on
  // montre ce que ça donnera pour éviter la confusion.
  const partTotal = (score.reps_completed || 0) + (score.reps_remaining || 0);
  const enteredReps = parseInt(reps) || 0;
  const capRemaining =
    score.reps_remaining != null ? Math.max(0, partTotal - enteredReps) : null;
  const previewText = finished
    ? (timeStr.trim() || 'mm:ss')
    : (capRemaining != null ? `CAP +${capRemaining}` : `CAP · ${enteredReps} reps`);

  // ===== Mobile : édition en bottom sheet (formulaire vertical, gros doigts) =====
  if (layout === 'sheet') {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-3 py-1.5 text-sm font-medium hover:bg-muted/50 active:bg-muted/70"
        >
          <span className={cn('tabular-nums', score.is_dns && 'text-destructive')}>{formatScoreDisplay(score, st)}</span>
          {st === 'time' && !score.is_dns && !score.is_finished && score.reps_completed > 0 && (
            <span className="text-[10px] text-muted-foreground">CAP</span>
          )}
          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
        </button>

        <Sheet open={isEditing} onOpenChange={(open) => { if (!isSaving) setIsEditing(open); }}>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <SheetHeader>
              <SheetTitle>{t('results.editScore')}</SheetTitle>
              {label && <SheetDescription>{label}</SheetDescription>}
            </SheetHeader>

            <div className="py-4 space-y-4">
              {st === 'time' && (
                <>
                  <div className="flex rounded-lg overflow-hidden border border-border">
                    <button type="button" onClick={() => setFinished(true)}
                      className={cn('flex-1 py-3 text-sm font-medium', finished ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>
                      {t('results.finished')}
                    </button>
                    <button type="button" onClick={() => setFinished(false)}
                      className={cn('flex-1 py-3 text-sm font-medium', !finished ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>
                      CAP
                    </button>
                  </div>
                  {finished ? (
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">{t('results.timeLabel')}</label>
                      <Input inputMode="numeric" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} placeholder="mm:ss" className="h-12 text-center text-lg" disabled={isSaving} />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">{t('results.repsDone')}</label>
                      <NumberInput inputMode="numeric" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="reps" className="h-12 text-center text-lg" disabled={isSaving} />
                    </div>
                  )}
                  <p className="text-center text-sm text-muted-foreground">
                    Rendu : <span className="font-medium text-foreground tabular-nums">{previewText}</span>
                  </p>
                </>
              )}

              {st === 'weight' && (
                <div className="space-y-1">
                  <label className="text-sm text-muted-foreground">{t('results.loadKg')}</label>
                  <NumberInput inputMode="decimal" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="kg" className="h-12 text-center text-lg" disabled={isSaving} />
                </div>
              )}

              {st !== 'time' && st !== 'weight' && (
                <div className="space-y-1">
                  <label className="text-sm text-muted-foreground">{t('results.repsLabel')}</label>
                  <NumberInput inputMode="numeric" value={reps} onChange={(e) => setReps(e.target.value)} placeholder="reps" className="h-12 text-center text-lg" disabled={isSaving} />
                </div>
              )}
            </div>

            <Button variant="ghost" className="w-full mb-2 text-muted-foreground" onClick={toggleAbsent} disabled={isSaving}>
              {absentLabel}
            </Button>
            <SheetFooter className="flex-row gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setIsEditing(false)} disabled={isSaving}>
                {t('common.cancel')}
              </Button>
              <Button className="flex-1" onClick={() => void persist(buildPayload())} disabled={isSaving}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div ref={wrapRef} className="relative inline-block min-w-[60px] text-center">
      {/* Valeur affichée — reste en place, garde la taille de la cellule */}
      <div
        onMouseDown={() => { if (!isEditing) setIsEditing(true); }}
        className={cn('cursor-pointer px-2 py-1 rounded', !isEditing && 'hover:bg-muted/50', score.is_dns && 'text-destructive')}
        title={t('results.editScore')}
      >
        {formatScoreDisplay(score, st)}
        {st === 'time' && !score.is_dns && !score.is_finished && score.reps_completed > 0 && (
          <span className="ml-1 text-[10px] text-muted-foreground align-middle">CAP</span>
        )}
      </div>

      {/* Éditeur en popover : même position, au-dessus du tableau (z-index), une seule ligne */}
      {isEditing && (
        <div
          className="absolute left-1/2 top-1/2 z-50 flex items-center gap-2 whitespace-nowrap rounded-lg border border-border/60 bg-popover px-2.5 py-1.5 shadow-2xl"
          style={{ transform: inputOffset != null ? `translate(${-inputOffset}px, -50%)` : 'translate(-50%, -50%)' }}
        >
          {st === 'time' && (
            <>
              <div className="flex rounded-md overflow-hidden border border-border text-xs">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setFinished(true)}
                  className={cn('px-3 py-1', finished ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>Terminé</button>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setFinished(false)}
                  className={cn('px-3 py-1', !finished ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}>CAP</button>
              </div>
              {finished ? (
                <Input ref={inputRef} autoFocus value={timeStr} onChange={(e) => setTimeStr(e.target.value)} onKeyDown={onKeyDown} placeholder="mm:ss" className={inputCls} disabled={isSaving} />
              ) : (
                <NumberInput ref={inputRef} autoFocus value={reps} onChange={(e) => setReps(e.target.value)} onKeyDown={onKeyDown} placeholder="reps" className={inputCls} disabled={isSaving} />
              )}
              <span className="text-[11px] text-muted-foreground tabular-nums select-none" aria-hidden="true">
                {previewText}
              </span>
            </>
          )}

          {st === 'weight' && (
            <NumberInput ref={inputRef} autoFocus inputMode="decimal" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} onKeyDown={onKeyDown} placeholder="kg" className={inputCls} disabled={isSaving} />
          )}

          {st !== 'time' && st !== 'weight' && (
            <NumberInput ref={inputRef} autoFocus value={reps} onChange={(e) => setReps(e.target.value)} onKeyDown={onKeyDown} placeholder="reps" className={inputCls} disabled={isSaving} />
          )}

          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={toggleAbsent} disabled={isSaving}
            title={absentLabel}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground">
            {score.is_dns ? t('results.present') : 'DNS'}
          </button>

          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
          ) : (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground select-none" aria-hidden="true">
              <kbd className="rounded border border-border px-1">↵</kbd>
              <kbd className="rounded border border-border px-1">esc</kbd>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const EventCrossFitResults: React.FC = () => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setSelectedEventId } = useEvent();

  // Cale le dropdown global sur l'événement de l'URL.
  useEffect(() => {
    if (eventId) setSelectedEventId(eventId);
  }, [eventId, setSelectedEventId]);

  // Vue courante : classement général (agrégat, lecture seule) ou par WOD.
  const [view, setView] = useState<ResultView>('general');
  // L'édition n'existe qu'en vue "par WOD".
  const [editMode, setEditMode] = useState(false);

  // Initialisation depuis ?edit=1 (deep-link de l'ancienne page Scores) → par WOD + édition.
  useEffect(() => {
    if (searchParams.get('edit') === '1') {
      setView('by-wod');
      setEditMode(true);
      setSearchParams(prev => { prev.delete('edit'); return prev; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // État commun
  const [wods, setWods] = useState<Wod[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedWodId, setSelectedWodId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  // Événement pour lequel les filtres (wod/catégorie) courants sont valides.
  const loadedEventRef = useRef<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWod, setSelectedWod] = useState<Wod | null>(null);
  const [results, setResults] = useState<TeamResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // État vue "classement général" (agrégat par catégorie)
  const [generalResults, setGeneralResults] = useState<CrossFitResult[]>([]);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Tri (vue par WOD)
  const [sortColumn, setSortColumn] = useState<string>('rank');
  const [sortAscending, setSortAscending] = useState(true);

  // Accordéon (revue UX 2026-08-03) : TOUTE ligne est dépliable — même un
  // score simple a un détail (heat, lane, finalisation) et c'est là que vit
  // l'accès aux signatures (pièce justificative d'un score contesté),
  // déplacé depuis la page Heats où il n'avait pas de contexte d'usage.
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [expandedGeneralTeamId, setExpandedGeneralTeamId] = useState<string | null>(null);
  const [signatureCtx, setSignatureCtx] = useState<{ heatId?: string; wodId?: string; heatName?: string; teamId: string } | null>(null);


  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTiebreakDisplay = (scores: Record<number, PartScore>): string => {
    const tiebreaks: string[] = [];
    for (const [, score] of Object.entries(scores)) {
      if (score && score.tiebreak_time !== null) {
        tiebreaks.push(formatTime(score.tiebreak_time));
      }
    }
    return tiebreaks.length > 0 ? tiebreaks.join(' | ') : '-';
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortAscending(!sortAscending);
    } else {
      setSortColumn(column);
      setSortAscending(true);
    }
  };

  const getScoreValue = (score: PartScore | undefined): number => {
    if (!score) return -999999;
    if (score.announced_weight != null) return score.announced_weight; // weight: plus lourd = mieux
    if (score.is_finished && score.finish_time_seconds && score.finish_time_seconds > 0) {
      return -score.finish_time_seconds; // time terminé: moins = mieux (négatif)
    }
    return score.reps_completed || 0; // reps / time cappé
  };

  // Bascule de vue. Passer en "général" désactive l'édition (pas d'édition sur l'agrégat).
  const handleViewChange = (value: string) => {
    const next = value as ResultView;
    setView(next);
    if (next === 'general') setEditMode(false);
  };

  // ===== Chargement vue "par WOD" (scores) =====
  useEffect(() => {
    loadScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, selectedWodId, selectedCategoryId]);

  // Temps réel (LOG-201) : un score finalisé sur le terrain apparaît ici sans
  // recharger. Toujours en `silent` — c'est un rafraîchissement de fond, jamais
  // demandé par l'organisateur : démonter la table refermerait l'éditeur qu'il
  // a peut-être ouvert sur une autre ligne.
  useRealtime(['crossfit_result'], () => { loadScores({ silent: true }); });

  const loadScores = async (opts?: { silent?: boolean }) => {
    if (!eventId) return;

    // Changement d'événement : les filtres wod/catégorie de l'ancien event ne
    // valent rien ici → on ne les envoie pas (sinon 404), et on re-sélectionne
    // le premier wod/catégorie du nouvel event.
    const freshEvent = loadedEventRef.current !== eventId;

    // silent : refetch sans démonter la table (sinon l'éditeur ouvert ailleurs
    // se ferme). Utilisé après une sauvegarde.
    if (!opts?.silent) setLoading(true);
    setError(null);

    try {
      // LOG-166 : apiService (axios) au lieu de fetch brut — profite de
      // l'intercepteur de refresh token (une session expirée se répare au
      // lieu d'afficher une erreur de chargement).
      const params: Record<string, string> = {};
      if (selectedWodId && !freshEvent) params.wod_id = selectedWodId;
      if (selectedCategoryId && !freshEvent) params.category_id = selectedCategoryId;
      if (searchQuery) params.search = searchQuery;

      const response = await apiService.get(`/events/${eventId}/scores`, params);
      const data = response.data;
      loadedEventRef.current = eventId;
      setWods(data.wods || []);
      setCategories(data.categories || []);
      setSelectedWod(data.selected_wod || null);
      setResults(data.results || []);

      if (freshEvent) {
        // Nouvel event : repartir sur son premier wod/catégorie.
        setSelectedWodId(data.wods?.[0]?.id || '');
        setSelectedCategoryId(data.categories?.[0]?.id || '');
      } else {
        // Auto-sélection si rien n'est sélectionné.
        if (!selectedWodId && data.wods?.length > 0) setSelectedWodId(data.wods[0].id);
        if (!selectedCategoryId && data.categories?.length > 0) setSelectedCategoryId(data.categories[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('results.unknownError'));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  const handleSaveScore = async (resultId: string, payload: Record<string, unknown>) => {
    try {
      await apiService.put(`/results/${resultId}`, payload);
      // Recharger les scores (silencieux : ne pas démonter la table, sinon
      // l'éditeur ouvert sur une autre cellule se ferme).
      await loadScores({ silent: true });
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError(t('results.saveScoreError'));
    }
  };

  // ===== Chargement vue "classement général" (agrégat par catégorie) =====
  useEffect(() => {
    if (view !== 'general') return;
    if (!eventId || !selectedCategoryId) return;
    let cancelled = false;
    (async () => {
      setGeneralLoading(true);
      setGeneralError(null);
      try {
        const resp = await crossfitResultsService.getGeneralResults(eventId, selectedCategoryId);
        if (!cancelled) setGeneralResults(resp.results);
      } catch (err: any) {
        if (!cancelled) {
          setGeneralError(err?.message || t('results.loadRankingError'));
          setGeneralResults([]);
        }
      } finally {
        if (!cancelled) setGeneralLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, selectedCategoryId, view]);

  // Filtrer et trier résultats (côté client) — vue par WOD
  const filteredResults = results
    .filter(r => r.team_name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      let valueA: number | string;
      let valueB: number | string;

      if (sortColumn === 'rank') {
        valueA = a.rank ?? 9999;
        valueB = b.rank ?? 9999;
      } else if (sortColumn === 'team') {
        valueA = a.team_name.toLowerCase();
        valueB = b.team_name.toLowerCase();
      } else if (sortColumn === 'category') {
        valueA = a.category_name.toLowerCase();
        valueB = b.category_name.toLowerCase();
      } else if (sortColumn === 'total') {
        valueA = a.total_reps || 0;
        valueB = b.total_reps || 0;
      } else if (sortColumn === 'points') {
        valueA = a.points || 0;
        valueB = b.points || 0;
      } else if (sortColumn.startsWith('part_') && sortColumn.endsWith('_score')) {
        const partNum = parseInt(sortColumn.replace('part_', '').replace('_score', ''));
        valueA = getScoreValue(a.scores[partNum]);
        valueB = getScoreValue(b.scores[partNum]);
      } else if (sortColumn.startsWith('part_') && sortColumn.endsWith('_pts')) {
        const partNum = parseInt(sortColumn.replace('part_', '').replace('_pts', ''));
        valueA = a.scores[partNum]?.category_points || 0;
        valueB = b.scores[partNum]?.category_points || 0;
      } else {
        valueA = a.rank ?? 9999;
        valueB = b.rank ?? 9999;
      }

      let comparison: number;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else {
        comparison = (valueA as number) - (valueB as number);
      }
      return sortAscending ? comparison : -comparison;
    });

  const partColumns = selectedWod?.parts || [];
  const hasMultipleParts = partColumns.length > 1;

  // Détail déplié d'une ligne (vue par WOD) : une ligne par partie (score,
  // points, tiebreak) + contexte de contestation (heat, lane, finalisation)
  // + accès à la signature de l'équipe.
  const renderTeamDetail = (result: TeamResult) => {
    const parts = partColumns.length > 0
      ? partColumns
      : [{ part_number: 1, name: selectedWod?.name || 'Score', scoring_type: selectedWod?.scoring_type }];
    const anyScore = Object.values(result.scores).find((s) => s?.heat_id) || Object.values(result.scores)[0];
    const heatId = anyScore?.heat_id || null;
    const heatNumber = anyScore?.heat_number ?? null;
    const laneNumber = anyScore?.lane_number ?? null;
    const finalizedAt = anyScore?.finalized_at || null;
    return (
      <div className="px-4 py-3 space-y-3">
        <div className="space-y-1.5">
          {parts.map((part) => {
            const score = result.scores[part.part_number];
            return (
              <div key={part.part_number} className="flex items-center justify-between text-sm gap-4">
                <span className="text-muted-foreground truncate">{part.name}</span>
                <span className="flex items-center gap-3 tabular-nums shrink-0">
                  <span>{formatScoreDisplay(score, part.scoring_type || selectedWod?.scoring_type)}</span>
                  {score?.cap_reached && !score.is_finished && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">CAP</Badge>
                  )}
                  {score?.tiebreak_time != null && (
                    <span className="text-xs text-muted-foreground">TB {formatTime(score.tiebreak_time)}</span>
                  )}
                  <span className="text-xs text-muted-foreground w-14 text-right">
                    {score?.category_points != null ? `${score.category_points.toFixed(0)} pts` : '—'}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2.5 text-xs text-muted-foreground flex-wrap">
          <span>
            {heatNumber != null ? `Heat #${heatNumber}` : t('results.detail.noHeat')}
            {laneNumber != null && ` · Lane ${laneNumber}`}
            {finalizedAt && ` · ${t('results.detail.finalizedAt')} ${new Date(finalizedAt).toLocaleString()}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              setSignatureCtx({
                heatId: heatId ?? undefined,
                wodId: selectedWodId || undefined,
                heatName: heatNumber != null ? `Heat #${heatNumber}` : undefined,
                teamId: result.team_id,
              });
            }}
          >
            <PenLine size={13} className="mr-1.5" />
            {t('results.detail.signature')}
          </Button>
        </div>
      </div>
    );
  };
  const readOnly = !editMode;

  // ===== Rendu : Classement général =====
  const renderGeneralView = () => {
    if (generalLoading) {
      return (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (generalError) {
      return <Alert variant="destructive"><AlertDescription>{generalError}</AlertDescription></Alert>;
    }
    if (generalResults.length === 0) {
      return (
        <Alert variant="info">
          <AlertDescription>{t('results.noResultsForCategory')}</AlertDescription>
        </Alert>
      );
    }
    return (
      <>
        {/* Desktop */}
        <div className="hidden md:block">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[34px]"></TableHead>
                    <TableHead>{t('results.rank')}</TableHead>
                    <TableHead>{t('results.team')}</TableHead>
                    <TableHead>{t('results.teamNumber')}</TableHead>
                    <TableHead>{t('results.category')}</TableHead>
                    <TableHead className="text-right">{t('results.totalPoints')}</TableHead>
                    <TableHead className="text-right">{t('results.wodsCompleted')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {generalResults.map((result) => {
                    const isOpen = expandedGeneralTeamId === result.team_id;
                    return (
                    <React.Fragment key={result.team_id}>
                    <TableRow
                      data-clickable=""
                      onClick={() => setExpandedGeneralTeamId(isOpen ? null : result.team_id)}
                      className={cn('cursor-pointer', result.rank && result.rank <= 3 && 'font-bold')}
                    >
                      <TableCell className="px-2">
                        <ChevronRight size={15} className={cn('text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                      </TableCell>
                      <TableCell>{result.rank}</TableCell>
                      <TableCell>{result.team_name}</TableCell>
                      <TableCell>{result.bib_number || '-'}</TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: result.category_color || '#007bff', color: '#fff' }}>
                          {result.category_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{result.total_points}</TableCell>
                      <TableCell className="text-right">{result.wods_completed}</TableCell>
                    </TableRow>
                                          <TableRow className={cn('bg-muted/10 hover:bg-muted/10', !isOpen && 'border-0')}>
                        <TableCell colSpan={7} className="p-0">
<div className={cn('grid transition-[grid-template-rows] duration-200 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
<div className="min-h-0 overflow-hidden">
                          <div className="px-4 py-3 space-y-1.5 font-normal">
                            {(result.wods || []).map((w) => (
                              <div key={w.wod_id} className="flex items-center justify-between text-sm gap-4">
                                <span className="text-muted-foreground truncate">{w.wod_name}</span>
                                <span className="flex items-center gap-3 shrink-0">
                                  <span className="text-xs text-muted-foreground tabular-nums">{w.points.toFixed(0)} pts</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-[11px]"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // heat_id peut être NULL (sync pré-LOG-165) →
                                      // repli sur la route résultat (event + wod).
                                      setSignatureCtx({
                                        heatId: w.heat_id ?? undefined,
                                        wodId: w.wod_id,
                                        heatName: w.heat_number != null ? `Heat #${w.heat_number}` : w.wod_name,
                                        teamId: result.team_id,
                                      });
                                    }}
                                  >
                                    <PenLine size={12} className="mr-1" />
                                    {t('results.detail.signature')}
                                  </Button>
                                </span>
                              </div>
                            ))}
                            {(!result.wods || result.wods.length === 0) && (
                              <span className="text-xs text-muted-foreground">{t('results.detail.noWodDetail')}</span>
                            )}
                          </div>
                        </div>
</div>
</TableCell>
                      </TableRow>
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-3">
          {generalResults.map((result) => {
            const isOpen = expandedGeneralTeamId === result.team_id;
            return (
            <Card key={result.team_id} className="cursor-pointer active:bg-muted/50" onClick={() => setExpandedGeneralTeamId(isOpen ? null : result.team_id)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'flex items-center justify-center w-10 h-10 rounded-full font-bold text-lg',
                      result.rank && result.rank <= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    )}>
                      {result.rank}
                    </div>
                    <div>
                      <p className="font-semibold">{result.team_name}</p>
                      <p className="text-sm text-muted-foreground">{t('results.teamNumber')} {result.bib_number || '-'}</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className={cn('mt-1 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('results.category')}</span>
                    <Badge style={{ backgroundColor: result.category_color || '#007bff', color: '#fff' }}>
                      {result.category_name}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('results.totalPoints')}</span>
                    <span className="font-bold text-lg">{result.total_points}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('results.wodsCompleted')}</span>
                    <span className="font-semibold">{result.wods_completed}</span>
                  </div>
                </div>
                                  <div className={cn('grid transition-[grid-template-rows] duration-200 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
<div className="min-h-0 overflow-hidden">
<div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    {(result.wods || []).map((w) => (
                      <div key={w.wod_id} className="flex items-center justify-between text-sm gap-3">
                        <span className="text-muted-foreground truncate">{w.wod_name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground tabular-nums">{w.points.toFixed(0)} pts</span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSignatureCtx({
                                heatId: w.heat_id ?? undefined,
                                wodId: w.wod_id,
                                heatName: w.heat_number != null ? `Heat #${w.heat_number}` : w.wod_name,
                                teamId: result.team_id,
                              });
                            }}
                          >
                            <PenLine size={12} className="mr-1" />
                            {t('results.detail.signature')}
                          </Button>
                        </span>
                      </div>
                    ))}
                    {(!result.wods || result.wods.length === 0) && (
                      <span className="text-xs text-muted-foreground">{t('results.detail.noWodDetail')}</span>
                    )}
                  </div>
</div>
</div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </>
    );
  };

  // ===== Rendu : Par WOD (lecture seule ou édition) =====
  const renderByWodView = () => (
    <>
      {/* Filtres WOD + recherche */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-end">
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">WOD</label>
          <Select value={selectedWodId} onValueChange={setSelectedWodId}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder={t('results.selectWod')} />
            </SelectTrigger>
            <SelectContent>
              {wods.map((wod) => (
                <SelectItem key={wod.id} value={wod.id}>{wod.wod_number}. {wod.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1 flex-1 md:flex-none">
          <label className="text-sm text-muted-foreground">{t('results.search')}</label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('results.searchTeamPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-full md:w-[200px]"
            />
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Desktop: Table */}
      {!loading && selectedWodId && (
        <div className="hidden md:block border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-[34px]"></TableHead>
                <TableHead className="w-[50px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('rank')}>
                  Rang {sortColumn === 'rank' && (sortAscending ? '↑' : '↓')}
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('team')}>
                  Équipe {sortColumn === 'team' && (sortAscending ? '↑' : '↓')}
                </TableHead>
                <TableHead className="w-[120px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('category')}>
                  Catégorie {sortColumn === 'category' && (sortAscending ? '↑' : '↓')}
                </TableHead>

                {hasMultipleParts ? (
                  partColumns.flatMap((part) => [
                    <TableHead key={`${part.part_number}_score`} className="w-[100px] text-center cursor-pointer hover:bg-muted/50" onClick={() => handleSort(`part_${part.part_number}_score`)}>
                      {part.name} {sortColumn === `part_${part.part_number}_score` && (sortAscending ? '↑' : '↓')}
                    </TableHead>,
                    <TableHead key={`${part.part_number}_pts`} className="w-[60px] text-center cursor-pointer hover:bg-muted/50" onClick={() => handleSort(`part_${part.part_number}_pts`)}>
                      Pts {sortColumn === `part_${part.part_number}_pts` && (sortAscending ? '↑' : '↓')}
                    </TableHead>
                  ])
                ) : (
                  <TableHead className="w-[100px] text-center cursor-pointer hover:bg-muted/50" onClick={() => handleSort('part_1_score')}>
                    Score {sortColumn === 'part_1_score' && (sortAscending ? '↑' : '↓')}
                  </TableHead>
                )}

                {hasMultipleParts && (
                  <TableHead className="w-[80px] text-center cursor-pointer hover:bg-muted/50" onClick={() => handleSort('total')}>
                    {t('results.total')} {sortColumn === 'total' && (sortAscending ? '↑' : '↓')}
                  </TableHead>
                )}

                <TableHead className="w-[80px] text-center cursor-pointer hover:bg-muted/50" onClick={() => handleSort('points')}>
                  {t('results.points')} {sortColumn === 'points' && (sortAscending ? '↑' : '↓')}
                </TableHead>
                <TableHead className="w-[80px] text-center">{t('results.tiebreak')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasMultipleParts ? 7 + partColumns.length * 2 : 7} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? t('results.noTeamFound') : t('results.noScoresForWod')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredResults.map((result, index) => {
                  const isOpen = expandedTeamId === result.team_id;
                  const colCount = hasMultipleParts ? 7 + partColumns.length * 2 : 7;
                  return (
                  <React.Fragment key={result.team_id}>
                  <TableRow
                    data-clickable=""
                    onClick={() => setExpandedTeamId(isOpen ? null : result.team_id)}
                    className={cn('cursor-pointer', index % 2 === 0 ? 'bg-background' : 'bg-muted/20')}
                  >
                    <TableCell className="px-2">
                      <ChevronRight size={15} className={cn('text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                    </TableCell>
                    <TableCell className="font-medium text-center">{result.rank || '-'}</TableCell>
                    <TableCell className="font-medium">{result.team_name}</TableCell>
                    <TableCell className="text-muted-foreground">{result.category_name}</TableCell>

                    {/* Les cellules de score restent le territoire de l'éditeur
                        inline : le clic n'y déclenche PAS l'accordéon. */}
                    {hasMultipleParts ? (
                      partColumns.flatMap((part) => {
                        const score = result.scores[part.part_number];
                        return [
                          <TableCell key={`${part.part_number}_score`} className="text-center" onClick={(e) => !readOnly && e.stopPropagation()}>
                            <ScoreEditCell score={score} scoringType={part.scoring_type || selectedWod?.scoring_type} readOnly={readOnly} onSave={handleSaveScore} />
                          </TableCell>,
                          <TableCell key={`${part.part_number}_pts`} className="text-center text-muted-foreground text-xs">
                            {score?.category_points ? `${score.category_points.toFixed(0)} pts` : '-'}
                          </TableCell>
                        ];
                      })
                    ) : (
                      <TableCell className="text-center" onClick={(e) => !readOnly && e.stopPropagation()}>
                        <ScoreEditCell score={result.scores[1]} scoringType={selectedWod?.parts?.[0]?.scoring_type || selectedWod?.scoring_type} readOnly={readOnly} onSave={handleSaveScore} />
                      </TableCell>
                    )}

                    {hasMultipleParts && (
                      <TableCell className="text-center font-medium text-primary">{result.total_reps}</TableCell>
                    )}

                    <TableCell className="text-center text-muted-foreground text-xs">
                      {result.points ? `${result.points.toFixed(0)} pts` : '-'}
                    </TableCell>

                    <TableCell className="text-center text-muted-foreground">{getTiebreakDisplay(result.scores)}</TableCell>
                  </TableRow>
                                      <TableRow className={cn('bg-muted/10 hover:bg-muted/10', !isOpen && 'border-0')}>
                      <TableCell colSpan={colCount} className="p-0">
<div className={cn('grid transition-[grid-template-rows] duration-200 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
<div className="min-h-0 overflow-hidden">
                        {renderTeamDetail(result)}
                      </div>
</div>
</TableCell>
                    </TableRow>
                  </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Mobile: Cards */}
      {!loading && selectedWodId && (
        <div className="md:hidden space-y-3">
          {filteredResults.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {searchQuery ? t('results.noTeamFound') : t('results.noScoresForWod')}
              </CardContent>
            </Card>
          ) : (
            filteredResults.map((result) => (
              <Card key={result.team_id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg font-bold text-primary shrink-0">#{result.rank || '-'}</span>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{result.team_name}</p>
                        <p className="text-xs text-muted-foreground">{result.category_name}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {result.points ? `${result.points.toFixed(0)} pts` : '-'}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {(partColumns.length > 0
                      ? partColumns
                      : [{ part_number: 1, name: 'Score', scoring_type: selectedWod?.scoring_type }]
                    ).map((part) => {
                      const score = result.scores[part.part_number];
                      return (
                        <div key={part.part_number} className="flex items-center justify-between gap-2 py-1 border-t border-border first:border-t-0">
                          <span className="text-sm text-muted-foreground">{hasMultipleParts ? part.name : 'Score'}</span>
                          <div className="flex items-center gap-3">
                            <ScoreEditCell
                              score={score}
                              scoringType={part.scoring_type || selectedWod?.scoring_type}
                              readOnly={readOnly}
                              layout="sheet"
                              label={`${result.team_name} · ${part.name || selectedWod?.name || 'Score'}`}
                              onSave={handleSaveScore}
                            />
                            {hasMultipleParts && score?.category_points != null && (
                              <span className="text-xs text-muted-foreground">{score.category_points.toFixed(0)} pts</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {(hasMultipleParts || getTiebreakDisplay(result.scores) !== '-') && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border text-sm">
                      {hasMultipleParts && (
                        <span className="font-medium text-primary">Total: {result.total_reps}</span>
                      )}
                      {getTiebreakDisplay(result.scores) !== '-' && (
                        <span className="text-xs text-muted-foreground">TB: {getTiebreakDisplay(result.scores)}</span>
                      )}
                    </div>
                  )}

                  {/* Contexte de contestation (mobile : la carte EST le détail) */}
                  {(() => {
                    const anyScore = Object.values(result.scores).find((s) => s?.heat_id) || Object.values(result.scores)[0];
                    if (!anyScore) return null;
                    return (
                      <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-border text-xs text-muted-foreground flex-wrap">
                        <span>
                          {anyScore.heat_number != null ? `Heat #${anyScore.heat_number}` : t('results.detail.noHeat')}
                          {anyScore.lane_number != null && ` · Lane ${anyScore.lane_number}`}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => setSignatureCtx({
                            heatId: anyScore.heat_id ?? undefined,
                            wodId: selectedWodId || undefined,
                            heatName: anyScore.heat_number != null ? `Heat #${anyScore.heat_number}` : undefined,
                            teamId: result.team_id,
                          })}
                        >
                          <PenLine size={12} className="mr-1" />
                          {t('results.detail.signature')}
                        </Button>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {!loading && !selectedWodId && wods.length > 0 && (
        <div className="text-center py-12 text-muted-foreground">Sélectionnez un WOD pour voir les scores</div>
      )}
      {!loading && wods.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">Aucun WOD configuré pour cet événement</div>
      )}
    </>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header : titre + bouton d'édition (par WOD uniquement) */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">{t('results.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {view === 'general'
              ? t('results.categoryRanking')
              : editMode
                ? t('results.editInTable')
                : t('results.scoresByWod')}
          </p>
        </div>

        {view === 'general' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button variant="outline" size="sm" disabled>
                    <Pencil className="w-4 h-4 mr-2" />
                    {t('common.edit')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{t('results.editOnlyByWod')}</p>
              </TooltipContent>
            </Tooltip>
        ) : (
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEditMode((v) => !v)}
          >
            {editMode ? <Check className="w-4 h-4 mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
            {editMode ? t('results.finishEditing') : t('common.edit')}
          </Button>
        )}
      </div>

      {/* Sélecteur de vue */}
      <Tabs value={view} onValueChange={handleViewChange}>
        <TabsList>
          <TabsTrigger value="general">{t('results.generalRanking')}</TabsTrigger>
          <TabsTrigger value="by-wod">{t('results.byWod')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filtre catégorie — obligatoire, commun aux deux vues */}
      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">{t('results.category')}</label>
        <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
          <SelectTrigger className="w-full md:w-[220px]">
            <SelectValue placeholder={t('results.category')} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {view === 'general' ? renderGeneralView() : renderByWodView()}

      {/* Pièce justificative d'un score : signature de l'équipe, filtrée
          sur l'équipe depuis laquelle on a ouvert le détail. */}
      <SignatureViewer
        open={!!signatureCtx}
        onClose={() => setSignatureCtx(null)}
        heatId={signatureCtx?.heatId}
        eventId={eventId}
        wodId={signatureCtx?.wodId}
        heatName={signatureCtx?.heatName}
        teamId={signatureCtx?.teamId}
      />
    </div>
  );
};

export default EventCrossFitResults;
